const chapter = {
  id: 'solid-state',
  slug: 'solid-state',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'The Solid State',
        hindi: 'ठोस अवस्था',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification of Solids',
        hindi: 'ठोसों का वर्गीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Crystalline**: long-range order; sharp m.p.; anisotropic; true solids. Examples: NaCl, quartz, metals.',
        hindi: '**क्रिस्टलीय**: लंबी दूरी का ऑर्डर; तीव्र एम.पी.; अनिसोट्रोपिक; असली ठोस. उदाहरण: NaCl, क्वार्टज़, धातुएँ।',
            },
            {
              english: '**Amorphous** (super-cooled liquids): short-range order; no sharp m.p.; isotropic; pseudo-solids. Examples: glass, rubber, plastic.',
        hindi: '**अनाकार** (सुपर-ठंडा तरल पदार्थ): कम दूरी का क्रम; कोई तीखा एम.पी. नहीं; आइसोट्रोपिक; छद्म ठोस. उदाहरण: कांच, रबर, प्लास्टिक।',
            },
            {
              english: 'Crystalline solids further classified by binding force:',
        hindi: 'क्रिस्टलीय ठोसों को बंधन बल द्वारा आगे वर्गीकृत किया गया है:',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Type', 'Constituent particles', 'Forces', 'Examples', 'm.p. / conduction'],
          rows: [
            ['Molecular — non-polar', 'Molecules', 'London dispersion', 'Ar, $H_2$, $CO_2$', 'Very low / insulator'],
            ['Molecular — polar', 'Polar molecules', 'Dipole–dipole', 'HCl, $SO_2$', 'Low / insulator'],
            ['Molecular — H-bonded', 'Polar with H', 'Hydrogen bonds', 'Ice, glucose', 'Low–moderate / insulator'],
            ['Ionic', 'Cations & anions', 'Coulombic', 'NaCl, $ZnS$, $CaF_2$', 'High / insulator solid; conductor in melt'],
            ['Metallic', 'Cations + e⁻ sea', 'Metallic bond', 'Cu, Fe, alloys', 'Wide range / conductor (solid & melt)'],
            ['Covalent / network', 'Atoms in covalent bonds', 'Covalent', 'Diamond, $SiO_2$, SiC', 'Very high / mostly insulator (graphite — conducts)'],
          ],
        },
      ],
    },
    {
      id: 'lattice',
      title: {
        english: 'Crystal Lattice & Unit Cell',
        hindi: 'क्रिस्टल Common Trapक और एकक कोश',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Crystal lattice** = regular 3-D arrangement of points; **unit cell** = smallest repeating block defined by edges $a, b, c$ and angles $\\alpha, \\beta, \\gamma$.',
        hindi: '**क्रिस्टल Common Trapी** = बिंदुओं की नियमित 3-डी व्यवस्था; **यूनिट सेल** = किनारों $a, b, c$ और कोणों $\\alpha, \\beta, \\gamma$ द्वारा परिभाषित सबसे छोटा दोहराव वाला ब्लॉक।',
            },
            {
              english: 'Seven crystal systems and 14 Bravais lattices.',
        hindi: 'सात क्रिस्टल प्रणालियां और 14 ब्रावैस Common Trapी।',
            },
            {
              english: 'Types of unit cells: **Primitive** (atoms at 8 corners), **BCC** (corners + 1 body centre), **FCC** (corners + 6 face centres), **End-centred**.',
        hindi: 'इकाई कोशिकाओं के प्रकार: **आदिम** (8 कोनों पर परमाणु), **BCC** (कोने + 1 शरीर केंद्र), **FCC** (कोने + 6 मुख केंद्र), **अंत-केंद्रित**।',
            },
            {
              english: 'Sharing: corner atom contributes 1/8, edge 1/4, face 1/2, body 1.',
        hindi: 'साझा करना: कोने का परमाणु 1/8, किनारा 1/4, चेहरा 1/2, बॉडी 1 योगदान देता है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Unit cell', 'Atoms / cell ($Z$)', 'Coord. no.', 'Packing fraction', 'Relation $a$ ↔ $r$'],
          rows: [
            ['Simple cubic (SC)', '1', '6', '52.4 %', '$a = 2r$'],
            ['Body-centred cubic (BCC)', '2', '8', '68.0 %', '$a = 4r/\\sqrt{3}$'],
            ['Face-centred cubic (FCC) / CCP', '4', '12', '74.0 %', '$a = 2\\sqrt{2}\\,r$'],
            ['Hexagonal close packing (HCP)', '6 (per hex unit)', '12', '74.0 %', '$c/a = 1.633$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Density of unit cell',
        hindi: 'यूनिट सेल का घनत्व',
          },
          statement: {
            english: '$$\\rho = \\dfrac{Z \\cdot M}{a^3 \\cdot N_A},$$ where $Z$ = atoms/cell, $M$ = molar mass (g/mol), $a$ = edge in cm, $N_A$ = Avogadro\'s number.',
        hindi: '$\\rho = \\dfrac{Z \\cdot M}{a^3 \\cdot N_A},$ जहां $Z$ = परमाणु/कोशिका, $M$ = दाढ़ द्रव्यमान (g/mol), $a$ = सेमी में किनारा, $N_A$ = एवोगैड्रो की संख्या।',
          },
        },
      ],
    },
    {
      id: 'packing',
      title: {
        english: 'Close Packing & Voids',
        hindi: 'निकट संकुलन और रिक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '2-D close packing: square (coord 4) vs hexagonal (coord 6).',
        hindi: '2-डी क्लोज पैकिंग: वर्गाकार (कोऑर्ड 4) बनाम हेक्सागोनल (कोऑर्ड 6)।',
            },
            {
              english: '3-D stacking: ABAB → **HCP**; ABCABC → **CCP / FCC**. Both 74 % packing, coord 12.',
        hindi: '3-डी स्टैकिंग: एबीएबी → **एचसीपी**; एबीसीएबीसी → **सीसीपी/एफसीसी**। दोनों 74% पैकिंग, समन्वय 12. **शून्य**: चतुष्फलकीय (चार गोले चारों ओर) और अष्टफलकीय (6 गोले चारों ओर)। गोले के में: चतुष्फलकीय रिक्तियाँ, अष्टफलकीय रिक्तियाँ।',
            },
            {
              english: '**Voids**: tetrahedral (4 spheres around) and octahedral (6 spheres around). In CCP/HCP of $N$ spheres: $2N$ tetrahedral voids, $N$ octahedral voids.',
        hindi: '**खाली जगहें**: चतुष्फलकीय (चारों ओर 4 गोले) और अष्टफलकीय (चारों ओर 6 गोले)। $N$ गोले के CCP/HCP में: $2N$ चतुष्फलकीय रिक्तियाँ, $N$ अष्टफलकीय रिक्तियाँ।',
            },
            {
              english: 'Radius ratio rules (cation/anion): 0.155–0.225 trigonal (3); 0.225–0.414 tetrahedral (4); 0.414–0.732 octahedral (6); 0.732–1.0 cubic (8).',
        hindi: 'त्रिज्या अनुपात नियम (cation/anion): 0.155–0.225 त्रिकोणीय (3); 0.225–0.414 चतुष्फलकीय (4); 0.414–0.732 अष्टफलकीय (6); 0.732-1.0 घन (8)। **सामान्य आयनिक संरचनाएं**: NaCl (Cl⁻ का FCC, सभी अष्टफलकीय रिक्तियों में Na⁺; समन्वय 6:6); सीएससीएल (बीसीसी-जैसा, समन्वय 8:8); ZnS-जस्ता मिश्रण (कुल मिलाकर S²⁻, Zn²⁺ का FCC। टेट्राहेड्रल; 4:4); CaF₂-फ़्लोराइट (FCC Ca²⁺, F⁻ सभी टेट्राहेड्रल में; 8:4); एंटीफ्लोराइट Na₂O उलटा (4:8); हीरा (कुल मिलाकर C के साथ C का FCC, cation/anion)।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Common ionic structures**: NaCl (FCC of Cl⁻, Na⁺ in all octahedral voids; coord 6:6); CsCl (BCC-like, coord 8:8); ZnS-zinc blende (FCC of S²⁻, Zn²⁺ in alt. tetrahedral; 4:4); CaF₂-fluorite (FCC Ca²⁺, F⁻ in all tetrahedral; 8:4); antifluorite Na₂O reversed (4:8); diamond (FCC of C with C in alt. tet voids, $Z=8$).',
        hindi: '**सामान्य आयनिक संरचनाएँ**: NaCl (सभी अष्टफलकीय रिक्तियों में Cl⁻, Na⁺ का FCC; समन्वय 6:6); सीएससीएल (बीसीसी-जैसा, समन्वय 8:8); ZnS-जस्ता मिश्रण (कुल मिलाकर S²⁻, Zn²⁺ का FCC। टेट्राहेड्रल; 4:4); CaF₂-फ़्लोराइट (FCC Ca²⁺, F⁻ सभी टेट्राहेड्रल में; 8:4); एंटीफ्लोराइट Na₂O उलटा (4:8); हीरा (कुल मिलाकर C के साथ C का FCC, $Z=8$)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'An FCC element of edge $a = 408$ pm. Atomic radius?',
        hindi: 'किनारे का एक FCC तत्व $a = 408$ अपराह्न। परमाणु त्रिज्या?',
          },
          solution: {
            english: '$r = a/(2\\sqrt{2}) = 408/2.828 \\approx 144.3$ pm.',
        hindi: '$r = a/(2\\sqrt{2}) = 408/2.828 \\approx 144.3$ अपराह्न।',
          },
        },
      ],
    },
    {
      id: 'defects',
      title: {
        english: 'Imperfections in Solids',
        hindi: 'ठोसों में अपूर्णताएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Point defects**:',
        hindi: '**बिंदु दोष**:',
            },
            {
              english: '• **Stoichiometric**: (i) **Schottky** — equal pairs of cation–anion vacancies; in ionic solids with similar-sized ions (NaCl, KCl); density decreases. (ii) **Frenkel** — cation displaced to interstitial; in solids with large size difference (AgCl, ZnS, AgBr); density unchanged.',
        hindi: '• **स्टोइकोमेट्रिक**: (i) **शॉट्की** - धनायन-आयन रिक्तियों के समान जोड़े; समान आकार के आयनों (NaCl, KCl) वाले आयनिक ठोसों में; घनत्व कम हो जाता है. (ii) **फ्रेनकेल** - धनायन अंतरालीय में विस्थापित; बड़े आकार के अंतर वाले ठोस पदार्थों में (AgCl, ZnS, AgBr); घनत्व अपरिवर्तित.',
            },
            {
              english: '• **Non-stoichiometric**: metal excess (e.g. NaCl heated in Na vapour gives anion-vacancy F-centres → yellow colour) or metal deficient (e.g. FeO often $Fe_{0.95}O$).',
        hindi: '• **नॉन-स्टोइकोमेट्रिक**: धातु की अधिकता (उदाहरण के लिए Na वाष्प में गरम किया गया NaCl ऋणायन-रिक्तता F-केंद्र → पीला रंग देता है) या धातु की कमी (उदाहरण के लिए FeO अक्सर $Fe_{0.95}O$)।',
            },
            {
              english: '• **Impurity defects**: e.g. SrCl₂ in NaCl introduces cation vacancies.',
        hindi: '• **अशुद्धि दोष**: उदा. NaCl में SrCl₂ धनायन रिक्तियों का परिचय देता है।',
            },
            {
              english: '**Line defects** (edge & screw dislocations) — important in mechanical strength.',
        hindi: '**लाइन दोष** (किनारे और पेंच अव्यवस्था) - यांत्रिक शक्ति में महत्वपूर्ण।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**F-centre** (Farbe centre) = anion vacancy occupied by an unpaired electron. Responsible for colour of non-stoichiometric crystals (NaCl yellow, KCl violet, LiCl pink).',
        hindi: '**एफ-केंद्र** (फ़ार्बे केंद्र) = एक अयुग्मित इलेक्ट्रॉन द्वारा व्याप्त आयन रिक्ति। गैर-स्टोइकोमेट्रिक क्रिस्टल (NaCl पीला, KCl बैंगनी, LiCl गुलाबी) के रंग के लिए जिम्मेदार।',
          },
        },
      ],
    },
    {
      id: 'electrical-magnetic',
      title: {
        english: 'Electrical & Magnetic Properties',
        hindi: 'विद्युत और चुम्बकीय गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Conductors** ($> 10^4$ S/m), **insulators** ($<10^{-8}$), **semiconductors** ($10^{-8}$–$10^4$). Band model: full valence band + empty conduction band; gap small in semiconductors.',
        hindi: '**कंडक्टर** ($> 10^4$ S/m), **इंसुलेटर** ($<10^{-8}$), **अर्धचालक** ($10^{-8}$–$10^4$)। बैंड मॉडल: पूर्ण वैलेंस बैंड + खाली चालन बैंड; अर्धचालकों में अंतराल छोटा.',
            },
            {
              english: 'Intrinsic semiconductors: pure Si, Ge. Extrinsic: **n-type** (doped with Gp 15 — extra electron); **p-type** (doped with Gp 13 — hole).',
        hindi: 'आंतरिक अर्धचालक: शुद्ध सी, जीई। बाह्य: **एन-प्रकार** (जीपी 15 के साथ डोप किया गया - अतिरिक्त इलेक्ट्रॉन); **पी-प्रकार** (जीपी 13 - छेद के साथ डोप किया गया)।',
            },
            {
              english: '**Magnetic behaviour**: diamagnetic (no unpaired e⁻, repelled), paramagnetic (unpaired e⁻, attracted), ferromagnetic (Fe, Co, Ni — domains aligned), antiferromagnetic ($MnO$, opposite domains cancel), ferrimagnetic ($Fe_3O_4$, opposite but unequal).',
        hindi: '**चुंबकीय व्यवहार**: प्रतिचुंबकीय (कोई अयुग्मित ई⁻, प्रतिकर्षित नहीं), अनुचुंबकीय (अयुग्मित ई⁻, आकर्षित), लौहचुंबकीय (Fe, Co, Ni - डोमेन संरेखित), प्रतिलौहचुंबकीय ($MnO$, विपरीत डोमेन रद्द), लौहचुंबकीय ($Fe_3O_4$, विपरीत लेकिन असमान)।',
            },
            {
              english: '**Piezoelectric** (charge under stress, e.g. quartz watches), **pyroelectric** (charge with $\\Delta T$), **ferroelectric** ($BaTiO_3$).',
        hindi: '**पीजोइलेक्ट्रिक** (तनाव के तहत चार्ज, उदाहरण के लिए क्वार्ट्ज घड़ियाँ), **पायरोइलेक्ट्रिक** ($\\Delta T$ के साथ चार्ज), **फेरोइलेक्ट्रिक** ($BaTiO_3$)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\rho = \\dfrac{Z\\,M}{a^3\\,N_A}',
      desc: {
        english: 'Density of unit cell',
        hindi: 'यूनिट सेल का घनत्व',
      },
    },
    {
      latex: 'a_{BCC} = 4r/\\sqrt{3},\\ a_{FCC} = 2\\sqrt{2}\\,r',
      desc: {
        english: 'Edge–radius relations',
        hindi: 'किनारा-त्रिज्या संबंध',
      },
    },
    {
      latex: 'PF_{SC}=52.4\\%,\\ PF_{BCC}=68\\%,\\ PF_{FCC/HCP}=74\\%',
      desc: {
        english: 'Packing fractions',
        hindi: 'अंशों की पैकिंग',
      },
    },
    {
      latex: '\\text{Tet voids} = 2N,\\ \\text{Oct voids} = N',
      desc: {
        english: 'In CCP/HCP of $N$ spheres',
        hindi: '$N$ क्षेत्रों में से CCP/HCP में',
      },
    },
    {
      latex: '0.414 \\le r^+/r^- \\le 0.732 \\Rightarrow \\text{octahedral}',
      desc: {
        english: 'Radius-ratio (NaCl-type)',
        hindi: 'त्रिज्या-अनुपात (NaCl-प्रकार)',
      },
    },
    {
      latex: 'AgCl: \\text{Frenkel};\\ NaCl: \\text{Schottky}',
      desc: {
        english: 'Defect examples',
        hindi: 'दोष उदाहरण',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Density',
      problem: {
        english: 'An FCC element ($M = 60$ g/mol) has $a = 400$ pm. Density?',
        hindi: 'एक FCC तत्व ($M = 60$ g/mol) का मूल्य $a = 400$ pm है। घनत्व?',
      },
      answer: {
        english: '≈ 6.23 g/cm³',
        hindi: '≈ 6.23 g/cm³',
      },
      solution: {
        english: '$\\rho = 4(60)/((4\\times 10^{-8})^3 \\cdot 6.022\\times 10^{23}) \\approx 6.23$.',
        hindi: '$\\rho = 4(60)/((4\\times 10^{-8})^3 \\cdot 6.022\\times 10^{23}) \\approx 6.23$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Voids',
      problem: {
        english: 'In a CCP arrangement of $N$ atoms, total number of octahedral and tetrahedral voids:',
        hindi: '$N$ परमाणुओं की सीसीपी व्यवस्था में, अष्टफलकीय और चतुष्फलकीय रिक्तियों की कुल संख्या:',
      },
      answer: {
        english: '$3N$',
        hindi: '$3N$',
      },
      solution: {
        english: 'Oct = N, Tet = 2N.',
        hindi: 'Oct = N, Tet = 2N।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Defect',
      problem: {
        english: 'AgBr shows which type of point defect?',
        hindi: 'AgBr किस प्रकार का बिंदु दोष दिखाता है?',
      },
      answer: {
        english: 'Both Schottky and Frenkel',
        hindi: 'शोट्की और फ्रेनकेल दोनों',
      },
      solution: {
        english: 'Mainly Frenkel due to size mismatch.',
        hindi: 'आकार बेमेल होने के कारण मुख्य रूप से फ्रेनकेल।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Coordination',
      problem: {
        english: 'Coordination numbers in CsCl crystal are:',
        hindi: 'CsCl क्रिस्टल में समन्वय संख्याएँ हैं:',
      },
      answer: {
        english: '8 : 8',
        hindi: '8 : 8',
      },
      solution: {
        english: 'BCC-like packing.',
        hindi: 'बीसीसी जैसी पैकिंग।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Semiconductor',
      problem: {
        english: 'Doping Si with P gives:',
        hindi: 'P के साथ डोपिंग Si देता है:',
      },
      answer: {
        english: 'n-type semiconductor',
        hindi: 'एन-प्रकार अर्धचालक',
      },
      solution: {
        english: 'P contributes one extra electron.',
        hindi: 'P एक अतिरिक्त इलेक्ट्रॉन का योगदान देता है।',
      },
    },
  ],
  next: {
    slug: 'solutions',
    title: 'Solutions',
  },
};

export default chapter;
